import React, { useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RefreshCw } from "lucide-react";
import { signatureservice } from "@/services/upload-service";
import { UploadSignatureModel } from "@/models/upload";

import {
  ROUTES,
  MODES,
  UI_TEXT,
  MESSAGES,
  CLASSES,
  MODETYPE,
  ALERT_TYPE,
  FILE_TYPE
} from "@/constants/common";

const initialForm: UploadSignatureModel = {
  imageId: null,
  lanId: "",
  subCompanyCode: "",
  firstName: "",
  lastName: "",
  title: "",
  sigType: "",
  lanIdIfInactive: "",
  managerLanId: "",
  file: null,
};

type NavState = { selectedId?: number; mode?: MODETYPE };

const UploadSignature: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [alert, setAlert] = useState({ type: "", message: "" });
  const state = (location.state || {}) as NavState;
  const idFromState = state?.selectedId ?? null;
  const mode = state?.mode ?? MODES.CREATE;

  const [loading, setLoading] = React.useState(false);
  const [form, setForm] = React.useState<UploadSignatureModel>(initialForm);
  const [editingId, setEditingId] = React.useState<string | number | null>(null);

  // Toggle First/Last/Title in edit or clone
  const isEditOrClone = mode === MODES.EDIT || mode === MODES.CLONE;
  const [enableCoreFields, setEnableCoreFields] = React.useState(!isEditOrClone);
  const lockCore = isEditOrClone && !enableCoreFields;

  const [lanLookupLoading, setLanLookupLoading] = React.useState(false);
  const lanLookupSeq = React.useRef(0);
  const lastLanQueriedRef = React.useRef<string>("");

  React.useEffect(() => {
    setEnableCoreFields(!isEditOrClone);
  }, [mode]);

  // ------------ Prefill for edit/clone ------------
  React.useEffect(() => {
    if (!idFromState) return;

    let cancelled = false;
    const getRecord = async () => {
      try {
        setLoading(true);
        const res = await signatureservice.getSignatureById(idFromState);
        const data = res?.data ?? {};

        if (!cancelled) {
          if (mode === MODES.EDIT) setEditingId(data.id ?? idFromState);

          setForm({
            imageId: idFromState ?? null,
            lanId: data.lanId ?? "",
            subCompanyCode: data.subCompanyCode ?? "",
            firstName: data.firstName ?? "",
            lastName: data.lastName ?? "",
            title: data.title ?? "",
            sigType: data.sigType ?? "",
            lanIdIfInactive: data.alternativeLanId ?? "",
            managerLanId: data.managerLanId ?? "",
            file: null,
          });
        }
      } catch (e: any) {
        if (!cancelled) handleExceptionMessage(e, "Failed to load records");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    getRecord();

    return () => {
      cancelled = true;
    };
  }, [idFromState, mode]);

  const handleLanIdBlur = React.useCallback(async () => {
    if (mode !== MODES.CREATE) return;

    const typedLan = form.lanId.trim();
    if (!typedLan) return;

    if (typedLan === lastLanQueriedRef.current) {
      return;
    }

    const mySeq = ++lanLookupSeq.current;
    setLanLookupLoading(true);

    try {
      const res = await signatureservice.getUserByLanId(encodeURIComponent(typedLan));
      const data = res?.data;

      if (!data || mySeq !== lanLookupSeq.current || !data.logonId) {
        lastLanQueriedRef.current = typedLan;
        setForm((prev) => ({
          ...prev,
          firstName: "",
          lastName: "",
          title: "",
        }));
        return;
      }

      lastLanQueriedRef.current = data.logonId;
      setForm((prev) => ({
        ...prev,
        lanId: data.logonId || prev.lanId,
        firstName: data.firstName ?? "",
        lastName: data.lastName ?? "",
        title: data.title ?? "",
      }));
    } catch {
      lastLanQueriedRef.current = typedLan;
      setForm((prev) => ({
        ...prev,
        firstName: "",
        lastName: "",
        title: "",
      }));
    } finally {
      setLanLookupLoading(false);
    }
  }, [form.lanId, mode]);

  // ------------ Handlers ------------
  const onChange =
    (key: keyof UploadSignatureModel) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((p) => ({ ...p, [key]: e.target.value }));
    };

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== FILE_TYPE.JPEG && file.type !== FILE_TYPE.PNG) {
      showAlert(ALERT_TYPE.ERROR, MESSAGES.ONLY_JPG_PNG);
      e.target.value = "";
      return;
    }
    setForm((p) => ({ ...p, file }));
  };

  function handleExceptionMessage(e: unknown, customMsg: string) {
    if (axios.isAxiosError(e)) {
      const d = e.response?.data;
      const serverMessage =
        typeof d === "string"
          ? d
          : d?.errors
            ? (Object.values(d.errors) as string[][]).flat().join(", ")
            : d?.title || MESSAGES.UNABLE_TO_CONNECT;

      showAlert(ALERT_TYPE.ERROR, serverMessage);
      return;
    }
    showAlert(ALERT_TYPE.ERROR, e instanceof Error ? e.message : customMsg);
  }

  const showAlert = (type: string, message: string) => {
    setAlert({ type, message });
    setTimeout(() => setAlert({ type: "", message: "" }), 5000);
  };

  // ------------ Validation ------------
  const canSubmit =
    !!form.lanId &&
    !!form.subCompanyCode &&
    !!form.firstName &&
    !!form.lastName &&
    !!form.title &&
    !!form.sigType &&
    (mode === MODES.EDIT ? true : !!form.file);

  // ------------ Submit ------------
  const buildFormData = () => {
    const fd = new FormData();
    fd.append("lanId", form.lanId);
    fd.append("subCompanyCode", form.subCompanyCode);
    fd.append("firstName", form.firstName);
    fd.append("lastName", form.lastName);
    fd.append("title", form.title);
    fd.append("sigType", form.sigType);
    if (form.lanIdIfInactive) fd.append("alternativeLanId", form.lanIdIfInactive);
    if (form.managerLanId) fd.append("managerLanId", form.managerLanId);
    if (form.file) fd.append("imageFile", form.file);
    if (editingId != null) fd.append("ImageId", String(editingId));
    return fd;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const fd = buildFormData();
      let res;
      if (editingId) {
        res = await signatureservice.updatesignature(fd);
      } else {
        res = await signatureservice.createsignature(fd);
      }
      const ok = res?.status && res.status >= 200 && res.status < 300;
      if (!ok) handleExceptionMessage(null, MESSAGES.OPERATION_FAILED);
      else {
        if (editingId) {
          showAlert(ALERT_TYPE.SUCCESS, MESSAGES.UPDATED_SUCCESS);
        } else {
          window.alert(MESSAGES.UPLOADED_SUCCESS);
          navigate(ROUTES.SIGNATURE_SEARCH, { replace: true });
        }
      }
    } catch (e: any) {
      handleExceptionMessage(e, MESSAGES.OPERATION_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const isEditing = Boolean(editingId);

  // --- Disable rules ---
  const disableLanId = mode === MODES.EDIT || mode === MODES.CLONE;
  const disableSubCompany = mode === MODES.EDIT;

  return (
    <div className={CLASSES.PAGE_WRAP}>
      <div className={CLASSES.PAGE_HEADER_ROW}>
        <h1 className={CLASSES.PAGE_TITLE}>{UI_TEXT.UPLOAD_SIGNATURE_PAGE_TITLE}</h1>

        {isEditOrClone && (
          <button
            onClick={() => setEnableCoreFields((p) => !p)}
            className={CLASSES.LINK_OVERRIDE}
          >
            {UI_TEXT.LINK_SPECIAL_OVERRIDE}
          </button>
        )}
      </div>

      <div className={CLASSES.FORM_GRID}>
        <div className={CLASSES.COL_STACK}>
          <div className="relative">
            <Label>
              {UI_TEXT.LABEL_LAN_ID} <span className={CLASSES.REQUIRED_MARK}>*</span>
            </Label>
            <Input
              value={form.lanId}
              onChange={onChange("lanId")}
              onBlur={handleLanIdBlur}
              disabled={disableLanId}
              readOnly={disableLanId}
              aria-disabled={disableLanId}
              aria-busy={lanLookupLoading}
            />
            {lanLookupLoading && (
              <div className={CLASSES.SPINNER_POS}>
                <RefreshCw className={CLASSES.SPINNER_ICON} />
              </div>
            )}
          </div>

          <div>
            <Label>
              {UI_TEXT.LABEL_SUB_COMPANY_CODE}{" "}
              <span className={CLASSES.REQUIRED_MARK}>*</span>
            </Label>
            <Input
              value={form.subCompanyCode}
              onChange={onChange("subCompanyCode")}
              disabled={disableSubCompany}
              readOnly={disableSubCompany}
              aria-disabled={disableSubCompany}
              title={disableSubCompany ? UI_TEXT.TOOLTIP_DISABLED_IN_EDIT : undefined}
            />
          </div>

          <div>
            <Label>
              {UI_TEXT.LABEL_FIRST_NAME} <span className={CLASSES.REQUIRED_MARK}>*</span>
            </Label>
            <Input
              value={form.firstName}
              onChange={onChange("firstName")}
              disabled={lockCore}
              readOnly={lockCore}
              aria-disabled={lockCore}
            />
          </div>

          <div>
            <Label>
              {UI_TEXT.LABEL_LAST_NAME} <span className={CLASSES.REQUIRED_MARK}>*</span>
            </Label>
            <Input
              value={form.lastName}
              onChange={onChange("lastName")}
              disabled={lockCore}
              readOnly={lockCore}
              aria-disabled={lockCore}
            />
          </div>
        </div>

        <div className={CLASSES.COL_STACK}>
          <div>
            <Label>
              {UI_TEXT.LABEL_TITLE} <span className={CLASSES.REQUIRED_MARK}>*</span>
            </Label>
            <Input
              value={form.title}
              onChange={onChange("title")}
              disabled={lockCore}
              readOnly={lockCore}
              aria-disabled={lockCore}
            />
          </div>

          <div>
            <Label>
              {UI_TEXT.LABEL_SIG_TYPE} <span className={CLASSES.REQUIRED_MARK}>*</span>
            </Label>
            <Input value={form.sigType} onChange={onChange("sigType")} />
          </div>

          <div>
            <Label>{UI_TEXT.LABEL_LAN_ID_IF_INACTIVE}</Label>
            <Input value={form.lanIdIfInactive ?? ""} onChange={onChange("lanIdIfInactive")} />
          </div>

          <div>
            <Label>{UI_TEXT.LABEL_MANAGER_LAN_ID}</Label>
            <Input value={form.managerLanId ?? ""} onChange={onChange("managerLanId")} />
          </div>
        </div>
      </div>

      {/* File */}
      <div className={CLASSES.FILE_BLOCK}>
        <Label>
          {UI_TEXT.LABEL_SIGNATURE_FILE}{" "}
          {mode === MODES.EDIT ? "" : <span className={CLASSES.REQUIRED_MARK}>*</span>}
        </Label>
        <Input type="file" accept={UI_TEXT.FILE_ACCEPT} onChange={onSelectFile} />
      </div>

      {/* Buttons */}
      <div className={CLASSES.ACTION_ROW}>
        <Button
          disabled={!canSubmit || loading}
          onClick={handleSubmit}
          className={CLASSES.BTN_PRIMARY_WIDE}
        >
          {loading && <RefreshCw className={CLASSES.SPINNER_ICON} />}
          {isEditing ? UI_TEXT.BTN_UPDATE : UI_TEXT.BTN_UPLOAD}
        </Button>

        <Button
          variant="secondary"
          onClick={() => navigate(ROUTES.SIGNATURE_SEARCH, { replace: true })}
        >
          {UI_TEXT.BTN_BACK}
        </Button>
      </div>

      {alert.message && (
        <div className={alert.type === ALERT_TYPE.SUCCESS ? CLASSES.ALERT_SUCCESS : CLASSES.ALERT_ERROR}>
          {alert.message}
        </div>
      )}
    </div>
  );
};

export default UploadSignature;